import { createBrowserRouter, Navigate } from 'react-router';
import { Suspense } from 'react';
import { AuthGuard } from './routes/_auth-guard';
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';

// Lazy-loaded layouts
const DashboardLayout = lazy(() => import('./routes/_dashboard-layout'));
const FullWidthDashboardLayout = lazy(() => import('./routes/_fullwidth-layout'));

// Auth pages
const LoginPage = lazy(() => import('./app/auth/login/page'));
const RegisterPage = lazy(() => import('./app/auth/register/page'));
const ForgotPasswordPage = lazy(() => import('./app/auth/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('./app/auth/reset-password/page'));

// Public pages
const LicensePage = lazy(() => import('./app/license/page'));
const SupportPage = lazy(() => import('./app/support/page'));
const TermsPage = lazy(() => import('./app/terms/page'));

// Dashboard pages
const DashboardHome = lazy(() => import('./app/dashboard/page'));
const MapPage = lazy(() => import('./app/dashboard/map/page'));
const CalendarPage = lazy(() => import('./app/dashboard/calendar/page'));
const SearchPage = lazy(() => import('./app/dashboard/search/page'));
const NotificationsPage = lazy(() => import('./app/dashboard/notifications/page'));
const ArchivePage = lazy(() => import('./app/dashboard/archive/page'));
const OpsPage = lazy(() => import('./app/dashboard/ops/page'));
const OpsConfigurationPage = lazy(() => import('./app/dashboard/ops/configuration/page'));
const OpsMobileAccessRequestsPage = lazy(() => import('./app/dashboard/ops/mobile-access-requests/page'));
const OpsSiteDetailPage = lazy(() => import('./app/dashboard/ops/sites/[id]/page'));
const GatewaysPage = lazy(() => import('./app/dashboard/gateways/page'));
const GatewayDetailPage = lazy(() => import('./app/dashboard/gateways/[id]/page'));
const CamerasPage = lazy(() => import('./app/dashboard/cameras/page'));
const CameraDetailPage = lazy(() => import('./app/dashboard/cameras/[id]/page'));
const NvrsPage = lazy(() => import('./app/dashboard/nvrs/page'));
const NvrDetailPage = lazy(() => import('./app/dashboard/nvrs/[id]/page'));

// Traffic
const TrafficEventsPage = lazy(() => import('./app/dashboard/traffic/events/page'));
const TrafficEventDetailPage = lazy(() => import('./app/dashboard/traffic/events/[id]/page'));
const TrafficEventsAuditPage = lazy(() => import('./app/dashboard/traffic/events/audit-report/page'));

const SeatbeltEventsPage = lazy(() => import('./app/dashboard/traffic/sbevents/page'));
const SeatbeltEventDetailPage = lazy(() => import('./app/dashboard/traffic/sbevents/[id]/page'));
const SeatbeltEventsAuditPage = lazy(() => import('./app/dashboard/traffic/sbevents/audit-report/page'));
const SpeedEventsPage = lazy(() => import('./app/dashboard/traffic/speedevents/page'));
const SpeedEventDetailPage = lazy(() => import('./app/dashboard/traffic/speedevents/[id]/page'));
const SpeedEventsAuditPage = lazy(() => import('./app/dashboard/traffic/speedevents/audit-report/page'));
const ViolationEventsPage = lazy(() => import('./app/dashboard/traffic/violations/page'));
const TrafficAnalyticsPage = lazy(() => import('./app/dashboard/traffic/analytics/page'));

// Sensors
const SensorsPage = lazy(() => import('./app/dashboard/sensors/page'));
const NewSensorPage = lazy(() => import('./app/dashboard/sensors/new/page'));
const SensorDetailPage = lazy(() => import('./app/dashboard/sensors/[id]/page'));
const SensorEditPage = lazy(() => import('./app/dashboard/sensors/[id]/edit/page'));
const SensorApiKeyPage = lazy(() => import('./app/dashboard/sensors/[id]/api-key/page'));

// Sensor Health
const SensorHealthPage = lazy(() => import('./app/dashboard/sensor-health/page'));
const SensorHealthDetailPage = lazy(() => import('./app/dashboard/sensor-health/[id]/page'));

// Locations
const LocationsPage = lazy(() => import('./app/dashboard/locations/page'));
const LocationDetailPage = lazy(() => import('./app/dashboard/locations/[id]/page'));
const LocationEditPage = lazy(() => import('./app/dashboard/locations/edit/[id]/page'));

// Incidents
const IncidentsPage = lazy(() => import('./app/dashboard/incidents/page'));
const NewIncidentPage = lazy(() => import('./app/dashboard/incidents/new/page'));
const IncidentDetailPage = lazy(() => import('./app/dashboard/incidents/[id]/page'));
const IncidentEditPage = lazy(() => import('./app/dashboard/incidents/[id]/edit/page'));

// Reports
const ReportsPage = lazy(() => import('./app/dashboard/reports/page'));
const ReportDetailPage = lazy(() => import('./app/dashboard/reports/[id]/page'));
const ReportGeneratePage = lazy(() => import('./app/dashboard/reports/generate/page'));

// Analytics
const AnalyticsPage = lazy(() => import('./app/dashboard/analytics/page'));

// Users
const UsersPage = lazy(() => import('./app/dashboard/users/page'));
const UserRolesPage = lazy(() => import('./app/dashboard/users/roles/page'));

// Settings
const SettingsPage = lazy(() => import('./app/dashboard/settings/page'));
const SettingsSpeedLimitsPage = lazy(() => import('./app/dashboard/settings/speed-limits/page'));
const SettingsSystemDefaultsPage = lazy(() => import('./app/dashboard/settings/system-defaults/page'));

// System
const SystemPage = lazy(() => import('./app/dashboard/system/page'));
const SystemAlertingPage = lazy(() => import('./app/dashboard/system/alerting/page'));
const SystemApiPage = lazy(() => import('./app/dashboard/system/api/page'));
const SystemHelpPage = lazy(() => import('./app/dashboard/system/help/page'));
const SystemLdapPage = lazy(() => import('./app/dashboard/system/ldap/page'));
const SystemMapboxPage = lazy(() => import('./app/dashboard/system/mapbox/page'));
const SystemMonitoringPage = lazy(() => import('./app/dashboard/system/monitoring/page'));
const SystemRetentionPage = lazy(() => import('./app/dashboard/system/retention/page'));
const SystemSecurityPage = lazy(() => import('./app/dashboard/system/security/page'));
const SystemTimezonePage = lazy(() => import('./app/dashboard/system/timezone/page'));
const SystemTranslationsPage = lazy(() => import('./app/dashboard/system/translations/page'));
const SystemWhitelabelPage = lazy(() => import('./app/dashboard/system/whitelabel/page'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-full min-h-[200px]">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Auth pages (no sidebar/header)
  {
    path: '/auth/login',
    element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
  },
  {
    path: '/auth/ops-support',
    element: <Navigate to="/auth/login?mode=ops-support&redirect=/dashboard/ops/configuration" replace />,
  },
  {
    path: '/auth/register',
    element: <SuspenseWrapper><RegisterPage /></SuspenseWrapper>,
  },
  {
    path: '/auth/forgot-password',
    element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>,
  },
  {
    path: '/auth/reset-password',
    element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>,
  },
  // Legacy login redirect
  {
    path: '/login',
    element: <Navigate to="/auth/login" replace />,
  },

  // Public pages
  {
    path: '/license',
    element: <SuspenseWrapper><LicensePage /></SuspenseWrapper>,
  },
  {
    path: '/support',
    element: <SuspenseWrapper><SupportPage /></SuspenseWrapper>,
  },
  {
    path: '/terms',
    element: <SuspenseWrapper><TermsPage /></SuspenseWrapper>,
  },

  // Protected routes
  {
    element: <AuthGuard />,
    children: [
      // Standard dashboard layout
      {
        element: <SuspenseWrapper><DashboardLayout /></SuspenseWrapper>,
        children: [
          { path: '/', element: <SuspenseWrapper><DashboardHome /></SuspenseWrapper> },
          { path: '/dashboard', element: <SuspenseWrapper><DashboardHome /></SuspenseWrapper> },
          { path: '/dashboard/map', element: <SuspenseWrapper><MapPage /></SuspenseWrapper> },
          { path: '/dashboard/calendar', element: <SuspenseWrapper><CalendarPage /></SuspenseWrapper> },
          { path: '/dashboard/search', element: <SuspenseWrapper><SearchPage /></SuspenseWrapper> },
          { path: '/dashboard/notifications', element: <SuspenseWrapper><NotificationsPage /></SuspenseWrapper> },
          { path: '/dashboard/archive', element: <SuspenseWrapper><ArchivePage /></SuspenseWrapper> },
          { path: '/dashboard/ops', element: <SuspenseWrapper><OpsPage /></SuspenseWrapper> },
          { path: '/dashboard/ops/configuration', element: <SuspenseWrapper><OpsConfigurationPage /></SuspenseWrapper> },
          { path: '/dashboard/ops/mobile-access-requests', element: <SuspenseWrapper><OpsMobileAccessRequestsPage /></SuspenseWrapper> },
          { path: '/dashboard/ops/sites/:id', element: <SuspenseWrapper><OpsSiteDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/gateways', element: <SuspenseWrapper><GatewaysPage /></SuspenseWrapper> },
          { path: '/dashboard/gateways/new', element: <SuspenseWrapper><NewSensorPage /></SuspenseWrapper> },
          { path: '/dashboard/gateways/:id', element: <SuspenseWrapper><GatewayDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/cameras', element: <SuspenseWrapper><CamerasPage /></SuspenseWrapper> },
          { path: '/dashboard/cameras/:id', element: <SuspenseWrapper><CameraDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/nvrs', element: <SuspenseWrapper><NvrsPage /></SuspenseWrapper> },
          { path: '/dashboard/nvrs/:id', element: <SuspenseWrapper><NvrDetailPage /></SuspenseWrapper> },

          // Traffic (standard width)
          { path: '/dashboard/traffic/analytics', element: <SuspenseWrapper><TrafficAnalyticsPage /></SuspenseWrapper> },

          { path: '/dashboard/traffic/speedevents', element: <SuspenseWrapper><SpeedEventsPage /></SuspenseWrapper> },
          { path: '/dashboard/traffic/speedevents/:id', element: <SuspenseWrapper><SpeedEventDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/traffic/speedevents/audit-report', element: <SuspenseWrapper><SpeedEventsAuditPage /></SuspenseWrapper> },
          { path: '/dashboard/traffic/violations', element: <SuspenseWrapper><ViolationEventsPage /></SuspenseWrapper> },

          // Sensors
          { path: '/dashboard/sensors', element: <SuspenseWrapper><SensorsPage /></SuspenseWrapper> },
          { path: '/dashboard/sensors/new', element: <SuspenseWrapper><NewSensorPage /></SuspenseWrapper> },
          { path: '/dashboard/sensors/:id', element: <SuspenseWrapper><SensorDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/sensors/:id/edit', element: <SuspenseWrapper><SensorEditPage /></SuspenseWrapper> },
          { path: '/dashboard/sensors/:id/api-key', element: <SuspenseWrapper><SensorApiKeyPage /></SuspenseWrapper> },

          // Sensor Health
          { path: '/dashboard/sensor-health', element: <SuspenseWrapper><SensorHealthPage /></SuspenseWrapper> },
          { path: '/dashboard/sensor-health/:id', element: <SuspenseWrapper><SensorHealthDetailPage /></SuspenseWrapper> },

          // Locations
          { path: '/dashboard/locations', element: <SuspenseWrapper><LocationsPage /></SuspenseWrapper> },
          { path: '/dashboard/locations/:id', element: <SuspenseWrapper><LocationDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/locations/edit/:id', element: <SuspenseWrapper><LocationEditPage /></SuspenseWrapper> },

          // Incidents
          { path: '/dashboard/incidents', element: <SuspenseWrapper><IncidentsPage /></SuspenseWrapper> },
          { path: '/dashboard/incidents/new', element: <SuspenseWrapper><NewIncidentPage /></SuspenseWrapper> },
          { path: '/dashboard/incidents/:id', element: <SuspenseWrapper><IncidentDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/incidents/:id/edit', element: <SuspenseWrapper><IncidentEditPage /></SuspenseWrapper> },

          // Reports
          { path: '/dashboard/reports', element: <SuspenseWrapper><ReportsPage /></SuspenseWrapper> },
          { path: '/dashboard/reports/generate', element: <SuspenseWrapper><ReportGeneratePage /></SuspenseWrapper> },
          { path: '/dashboard/reports/:id', element: <SuspenseWrapper><ReportDetailPage /></SuspenseWrapper> },

          // Analytics
          { path: '/dashboard/analytics', element: <SuspenseWrapper><AnalyticsPage /></SuspenseWrapper> },

          // Users
          { path: '/dashboard/users', element: <SuspenseWrapper><UsersPage /></SuspenseWrapper> },
          { path: '/dashboard/users/roles', element: <SuspenseWrapper><UserRolesPage /></SuspenseWrapper> },

          // Settings
          { path: '/dashboard/settings', element: <SuspenseWrapper><SettingsPage /></SuspenseWrapper> },
          { path: '/dashboard/settings/speed-limits', element: <SuspenseWrapper><SettingsSpeedLimitsPage /></SuspenseWrapper> },
          { path: '/dashboard/settings/system-defaults', element: <SuspenseWrapper><SettingsSystemDefaultsPage /></SuspenseWrapper> },

          // System
          { path: '/dashboard/system', element: <SuspenseWrapper><SystemPage /></SuspenseWrapper> },
          { path: '/dashboard/system/alerting', element: <SuspenseWrapper><SystemAlertingPage /></SuspenseWrapper> },
          { path: '/dashboard/system/api', element: <SuspenseWrapper><SystemApiPage /></SuspenseWrapper> },
          { path: '/dashboard/system/help', element: <SuspenseWrapper><SystemHelpPage /></SuspenseWrapper> },
          { path: '/dashboard/system/ldap', element: <SuspenseWrapper><SystemLdapPage /></SuspenseWrapper> },
          { path: '/dashboard/system/mapbox', element: <SuspenseWrapper><SystemMapboxPage /></SuspenseWrapper> },
          { path: '/dashboard/system/monitoring', element: <SuspenseWrapper><SystemMonitoringPage /></SuspenseWrapper> },
          { path: '/dashboard/system/retention', element: <SuspenseWrapper><SystemRetentionPage /></SuspenseWrapper> },
          { path: '/dashboard/system/security', element: <SuspenseWrapper><SystemSecurityPage /></SuspenseWrapper> },
          { path: '/dashboard/system/timezone', element: <SuspenseWrapper><SystemTimezonePage /></SuspenseWrapper> },
          { path: '/dashboard/system/translations', element: <SuspenseWrapper><SystemTranslationsPage /></SuspenseWrapper> },
          { path: '/dashboard/system/whitelabel', element: <SuspenseWrapper><SystemWhitelabelPage /></SuspenseWrapper> },
        ],
      },
      // Full-width layout (traffic events, seatbelt events)
      {
        element: <SuspenseWrapper><FullWidthDashboardLayout /></SuspenseWrapper>,
        children: [
          { path: '/dashboard/traffic/events', element: <SuspenseWrapper><TrafficEventsPage /></SuspenseWrapper> },
          { path: '/dashboard/traffic/events/:id', element: <SuspenseWrapper><TrafficEventDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/traffic/events/audit-report', element: <SuspenseWrapper><TrafficEventsAuditPage /></SuspenseWrapper> },
          { path: '/dashboard/traffic/sbevents', element: <SuspenseWrapper><SeatbeltEventsPage /></SuspenseWrapper> },
          { path: '/dashboard/traffic/sbevents/:id', element: <SuspenseWrapper><SeatbeltEventDetailPage /></SuspenseWrapper> },
          { path: '/dashboard/traffic/sbevents/audit-report', element: <SuspenseWrapper><SeatbeltEventsAuditPage /></SuspenseWrapper> },
        ],
      },
    ],
  },

  // Catch-all 404
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
]);
